import { CommonModule, DOCUMENT } from '@angular/common';
import {
    ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject, Injectable, OnDestroy, OnInit
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, combineLatest, distinctUntilChanged, filter, map, Observable, startWith, Subject } from 'rxjs';

import { environment } from '../../../environments/environment';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Doc360HostWindow, isPublicDashboardPath, showDoc360Widget } from './doc360-widget';

@Injectable({
    providedIn: 'root'
})
export class Doc360Service {
    private openWidget$ = new Subject<string | null>();

    /**
     * Opens the Doc360 widget, optionally to a specific document/article
     * @param articleId - Optional article ID to navigate to. If not provided, opens the main widget.
     */
    openDoc360Widget(articleId?: string): void {
        this.openWidget$.next(articleId || null);
    }

    getOpenWidget(): Observable<string | null> {
        return this.openWidget$.asObservable();
    }
}

@Component({
    selector: 'liq-doc360',
    standalone: true,
    template: `
        <button *ngIf="fabVisible$ | async" class="doc360-fab" (click)="openWidget()" aria-label="Document 360">
            <img src="/assets/icons/svgs/Document360.svg" alt="Doc360">
        </button>
    `,
    styles: [`
        .doc360-fab {
            position: fixed;
            bottom: 20px;
            right: 20px;
            /* Just under .cdk-overlay-container (1052, @theme/styles.scss) so dialogs, drawers and menus cover the FAB. */
            z-index: 1051;
            background-color: #ab2180;
            border: none;
            height: 45px;
            width: 45px;
        }

        .doc360-fab img {
            width: 30px;
        }
    `],
    imports: [CommonModule],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class LiqDoc360Component implements OnInit, OnDestroy {
    private document: Document = inject(DOCUMENT);
    private router = inject(Router);
    private destroyRef = inject(DestroyRef);
    private window = this.document.defaultView as Doc360HostWindow | null;
    private initFallbackHandle: ReturnType<typeof setTimeout> | null = null;
    private readonly initFallbackMs = 2000;
    /** True after init/show/fallback has armed the FAB — ignores premature hide signals during V2 boot. */
    private fabArmed = false;
    /** True while Widget V2 reports the panel open via postMessage. */
    private widgetOpen = false;
    private readonly onWindowMessage = (event: MessageEvent): void => {
        this.handleDoc360Message(event);
    };

    private widgetRequested = false;
    /** Our FAB ignores Doc360 hide_callback (LIQ-1814), so URL "Hide widget" rules can't hide it — gate here. */
    private onPublicPage$ = new BehaviorSubject(false);

    showButton$ = new BehaviorSubject(false);
    fabVisible$ = combineLatest([this.showButton$, this.onPublicPage$]).pipe(
        map(([show, onPublicPage]) => show && !onPublicPage)
    );

    constructor(
        private cdRef: ChangeDetectorRef,
        private doc360: Doc360Service
    ) {
        this.doc360.getOpenWidget().pipe(
            takeUntilDestroyed()
        ).subscribe(() => {
            this.openWidget();
        });
    }

    ngOnInit(): void {
        this.fabVisible$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            // for some reason, the change on its own is not causing the UI
            // to rerender in all cases, mainly on the login page.
            setTimeout(() => this.cdRef.detectChanges());
        });

        if (!environment.doc360APIKey) {
            return;
        }

        if (!this.window) {
            return;
        }

        this.router.events.pipe(
            filter((event): event is NavigationEnd => event instanceof NavigationEnd),
            map((event) => isPublicDashboardPath(event.urlAfterRedirects)),
            startWith(isPublicDashboardPath(this.document.location.pathname)),
            distinctUntilChanged(),
            takeUntilDestroyed(this.destroyRef)
        ).subscribe((onPublicPage) => {
            this.onPublicPage$.next(onPublicPage);
            if (!onPublicPage) {
                this.loadWidget();
            }
        });
    }

    /** Loads the Doc360 script once, on the first non-public page — public dashboards never get it. */
    private loadWidget(): void {
        if (this.widgetRequested || !this.window) {
            return;
        }
        this.widgetRequested = true;

        this.window['doc360_callback'] = this.initCallback.bind(this);
        this.window['doc360_show_callback'] = this.showCallback.bind(this);
        this.window['doc360_hide_callback'] = this.hideCallback.bind(this);
        this.window.addEventListener('message', this.onWindowMessage);

        const loaded = !!this.document.querySelector('#doc360');
        if (!loaded) {
            const script = this.document.createElement('script');
            script.id = 'doc360';
            script.innerHTML = `
                (function (w,d,s,o,f,js,fjs) {
                    w['JS-Widget']=o;w[o] = w[o] || function () { (w[o].q = w[o].q || []).push(arguments) };
                    js = d.createElement(s), fjs = d.getElementsByTagName(s)[0];
                    js.id = o; js.src = f; js.async = 1; fjs.parentNode.insertBefore(js, fjs);
                }(window, document, 'script', 'mw', 'https://cdn.document360.io/static/js/widget.js'));
                mw('init', { apiKey: '${environment.doc360APIKey}', callback: doc360_callback, show_callback: doc360_show_callback, hide_callback: doc360_hide_callback });
            `;
            this.document.body.appendChild(script);
        }
        this.scheduleInitFallback();
    }

    initCallback(): void {
        if (!this.widgetOpen) {
            this.armFab();
        }
    }

    showCallback(): void {
        // Legacy/URL-mapping callback. Do not re-show while the widget panel is open.
        if (!this.widgetOpen) {
            this.armFab();
        }
    }

    hideCallback(): void {
        // Widget V2 open/close is driven by postMessage (d360:show / d360:hide).
        // hide_callback still fires during boot and is unreliable for close — ignore for FAB.
    }

    private armFab(): void {
        this.fabArmed = true;
        this.clearInitFallback();
        this.showButton$.next(true);
    }

    private hideFab(): void {
        if (!this.fabArmed) {
            return;
        }
        this.showButton$.next(false);
    }

    private handleDoc360Message(event: MessageEvent): void {
        if (!this.isDoc360Origin(event.origin)) {
            return;
        }

        const type = this.getDoc360MessageType(event.data);
        if (type === 'd360:show') {
            this.widgetOpen = true;
            this.hideFab();
            return;
        }
        if (type === 'd360:hide') {
            this.widgetOpen = false;
            this.armFab();
        }
    }

    private isDoc360Origin(origin: string): boolean {
        return origin === 'https://cdn.document360.io'
            || origin === 'https://cdn.us.document360.io';
    }

    private getDoc360MessageType(data: unknown): string | null {
        if (!data || typeof data !== 'object') {
            return null;
        }
        const type = (data as { type?: unknown }).type;
        return typeof type === 'string' ? type : null;
    }

    ngOnDestroy(): void {
        this.clearInitFallback();
        this.window?.removeEventListener('message', this.onWindowMessage);
    }

    openWidget(): void {
        if (!this.window || this.onPublicPage$.value) {
            return;
        }
        showDoc360Widget(this.window, this.document);
    }

    private clearInitFallback(): void {
        if (this.initFallbackHandle !== null) {
            clearTimeout(this.initFallbackHandle);
            this.initFallbackHandle = null;
        }
    }

    private scheduleInitFallback(): void {
        this.clearInitFallback();
        this.initFallbackHandle = setTimeout(() => {
            if (!this.fabArmed) {
                this.armFab();
            }
            this.initFallbackHandle = null;
        }, this.initFallbackMs);
    }
}
